import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAsyncState } from '@vueuse/core'
import type { Notification } from '@/types/notification'
import {
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
} from '@/api/notificationApi'
import { useAuthStore } from '@/stores/auth.store.ts'

export const useNotificationStore = defineStore('notification', () => {
    const notifications = ref<Notification[]>([])
    const lastFetchTime = ref<Date | null>(null)

    /**
     * Computed: Count of unread notifications
     */
    const unreadCount = computed(() => {
        return notifications.value.filter((n) => !n.read).length
    })

    /**
     * Computed: Check if there are any unread notifications
     */
    const hasUnread = computed(() => unreadCount.value > 0)

    /**
     * Computed: Unread notifications only
     */
    const unreadNotifications = computed(() => {
        return notifications.value.filter((n) => !n.read)
    })

    /**
     * Fetch notifications from API
     */
    const { isLoading, execute: fetchAll } = useAsyncState(
        async () => {
            const authStore = useAuthStore()
            const userId = authStore.user?.id

            if (!userId) {
                return
            }

            const response = await fetchNotifications()
            notifications.value = response.data
            lastFetchTime.value = new Date()
        },
        undefined,
        { immediate: false }
    )

    /**
     * Mark a single notification as read
     */
    async function markAsRead(notificationId: string | undefined): Promise<void> {
        if (!notificationId) {
            return
        }

        try {
            await markNotificationAsRead(notificationId)

            // Update local state
            const notification = notifications.value.find(
                (n) => n.documentId === String(notificationId)
            )
            if (notification) {
                notification.read = true
            }
        } catch (error) {
            // HTTP Interceptor handles error toast
            console.error('Failed to mark notification as read:', error)
        }
    }

    /**
     * Mark all notifications as read
     */
    async function markAllAsRead(): Promise<void> {
        try {
            await markAllNotificationsAsRead()

            // Update local state
            notifications.value.forEach((notification) => {
                notification.read = true
            })
        } catch (error) {
            // HTTP Interceptor handles error toast
            console.error('Failed to mark all notifications as read:', error)
        }
    }

    /**
     * Clear all notifications from store
     */
    function clearNotifications(): void {
        notifications.value = []
        lastFetchTime.value = null
    }

    /**
     * Add a notification (for real-time updates via WebSocket, etc.)
     */
    function addNotification(notification: Notification): void {
        // Add to the beginning of the array (newest first)
        notifications.value.unshift(notification)
    }

    return {
        // State
        notifications,
        isLoading,
        lastFetchTime,

        // Computed
        unreadCount,
        hasUnread,
        unreadNotifications,

        // Actions
        fetchAll,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        addNotification,
    }
})
